import { useState, useEffect, useMemo } from "react";
import { notification } from "antd";
import { obtenerArchivos, eliminarArchivos, calcularEstadisticas } from "../../services/InformeActas/InformeActasService";

export function useFileManager(rutaRelativa, isModalOpen = true, onFilesDeleted, rutaActividad, pageSize = 10) {
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [filtroNombre, setFiltroNombre] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState([]);

  const openNotification = ({ type, message: title, description }) => {
    notification[type]({
      message: title,
      description,
      placement: "topRight",
      duration: 3,
    });
  };

  useEffect(() => {
    if (isModalOpen) {
      cargarArchivos();
    }
  }, [rutaRelativa, isModalOpen]);

  // Resetear a página 1 cuando cambien los filtros o el pageSize
  useEffect(() => {
    setCurrentPage(1);
  }, [filtroUsuario, filtroNombre, pageSize]);

  const cargarArchivos = async () => {
    try {
      setLoading(true);
      setError(null);
      const tmpcodActividadParametro = String(rutaActividad.codigo_tarea.codActividad).replace(/\./g, "");
      const fullPath = `DocumentosPOA/actividad_${tmpcodActividadParametro}/${rutaRelativa.path}`;
      const response = await obtenerArchivos({ rutaRelativa: fullPath });
      if (response.success) {
        setArchivos(response.data);
      } else {
        setError(response.message || "Error al cargar archivos");
        openNotification({
          type: "error",
          message: "Error",
          description: response.message || "Error al cargar archivos",
        });
      }
    } catch (err) {
      const errorMessage = "No se pudieron cargar los archivos";
      setError(errorMessage);
      openNotification({
        type: "error",
        message: "Error",
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // Archivos filtrados
  const archivosFiltrados = useMemo(() => {
    return archivos.filter((archivo) => {
      const matchUsuario =
        !filtroUsuario || archivo.json?.user?.toLowerCase().includes(filtroUsuario.toLowerCase());
      const matchNombre =
        !filtroNombre || archivo.nombre.toLowerCase().includes(filtroNombre.toLowerCase());
      return matchUsuario && matchNombre;
    });
  }, [archivos, filtroUsuario, filtroNombre]);

  // Paginación dinámica
  const totalPages = Math.ceil(archivosFiltrados.length / pageSize);
  const archivosPaginados = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return archivosFiltrados.slice(startIndex, startIndex + pageSize);
  }, [archivosFiltrados, currentPage, pageSize]);

  // Archivos totales (sin paginar) para mostrar en estadísticas
  const archivosTotales = useMemo(() => archivosFiltrados, [archivosFiltrados]);

  const filters = useMemo(
    () => ({
      usuario: filtroUsuario,
      nombre: filtroNombre,
    }),
    [filtroUsuario, filtroNombre]
  );

  const setFilters = (newFilters) => {
    if (newFilters.usuario !== undefined) setFiltroUsuario(newFilters.usuario);
    if (newFilters.nombre !== undefined) setFiltroNombre(newFilters.nombre);
    setCurrentPage(1);
  };

  // Usuarios únicos para el filtro
  const usuariosUnicos = useMemo(() => {
    const usuarios = new Set(archivos.map((a) => a.json?.user).filter(Boolean));
    return Array.from(usuarios).sort();
  }, [archivos]);

  // Selección de archivos
  const toggleFileSelection = (ruta) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(ruta)) {
      newSelected.delete(ruta);
    } else {
      newSelected.add(ruta);
    }
    setSelectedFiles(newSelected);
  };

  const toggleAllFiles = () => {
    if (selectedFiles.size === archivosPaginados.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(archivosPaginados.map((a) => a.ruta)));
    }
  };

  const clearSelection = () => {
    setSelectedFiles(new Set());
  };

  const deleteFiles = async (observacion, usuario) => {
    try {
      const archivosABorrar = filesToDelete;
      const rutas = archivosABorrar.map((f) => f.ruta);
      const response = await eliminarArchivos(rutas, observacion, usuario);

      if (response.success) {
        openNotification({
          type: "success",
          message: "Éxito",
          description: `${response.archivosEliminados.length} archivo(s) eliminado(s)`,
        });

        await cargarArchivos();
        clearSelection();
        setDeleteDialogOpen(false);
        setFilesToDelete([]);

        // ✅ Sincroniza el árbol de FileManagerPOA quitando los nodos borrados
        // localmente, sin recargar todo el árbol desde el backend
        if (typeof onFilesDeleted === 'function') {
          onFilesDeleted(archivosABorrar);
        }

        return response;
      }
    } catch (err) {
      openNotification({
        type: "error",
        message: "Error",
        description: "No se pudieron eliminar los archivos",
      });
      throw err;
    }
  };

  // Estadísticas - usar archivosTotales en lugar de archivosFiltrados para que incluya todos los archivos filtrados
  const stats = useMemo(() => calcularEstadisticas(archivosTotales), [archivosTotales]);

 // En el return del hook, agregar:
return {
  files: archivosPaginados,
  allFiles: archivosTotales,
  loading,
  error,
  filters,
  setFilters,
  selectedFiles,
  toggleFileSelection,
  toggleAllFiles,
  currentPage,
  setCurrentPage,
  totalPages,
  paginatedFiles: archivosPaginados,
  stats,
  deleteFiles,
  deleteDialogOpen,
  setDeleteDialogOpen,
  filesToDelete,
  setFilesToDelete,
  refreshFiles: cargarArchivos,
  usuariosUnicos,
  totalFiles: archivosFiltrados.length,
};
}